import { useDeferredValue, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Tags } from 'lucide-react';
import { PERMISSIONS, type ProductCategorySummary } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { FormField } from '@/components/ui/FormField';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { PageHeader } from '@/components/ui/PageHeader';
import { Select } from '@/components/ui/Select';
import { usePermissions } from '@/hooks/usePermissions';
import { apiErrorDetails } from '@/services/apiClient';
import { localizedName } from '@/modules/catalog/category-label';
import {
  useCategoryListQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
} from '../api/inventory.api';

/**
 * The tenant's product categories: how the shop groups what it sells, independent of the product type.
 *
 * A category such as «كليب أون» is ordinary tenant data here, never a rule in code; products keep their type (a clip-on is a frame or sunglasses underneath). Categories nest one level and are taken out of use rather than deleted, so products already filed under one keep it.
 */
export function ProductCategoriesPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canCreate = can(PERMISSIONS.CATALOG_CREATE);
  const canEdit = can(PERMISSIONS.CATALOG_EDIT);

  const [search, setSearch] = useState('');
  const [active, setActive] = useState<'all' | 'true' | 'false'>('all');
  const [editing, setEditing] = useState<ProductCategorySummary | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [name, setName] = useState('');
  const [nameAr, setNameAr] = useState('');
  const [parentId, setParentId] = useState('');
  const [feedback, setFeedback] = useState<string | null>(null);

  const deferredSearch = useDeferredValue(search);
  const { data, isPending, isError, refetch } = useCategoryListQuery({
    search: deferredSearch,
    isActive: active === 'all' ? null : active === 'true',
  });
  // Parents come from the full list, so a category out of use can still be seen as one.
  const { data: everything } = useCategoryListQuery({ search: '', isActive: null });

  const create = useCreateCategoryMutation();
  const update = useUpdateCategoryMutation();
  const saving = create.isPending || update.isPending;
  const saveError = create.error ?? update.error;

  const display = (category: { name: string; nameAr: string | null }) =>
    localizedName(category.name, category.nameAr, i18n.language);

  // Server refusals in the user's language: a taken name, or a parent that would make a third level.
  const describeSaveError = (error: unknown): string => {
    const fields = apiErrorDetails(error).map((detail) => detail.field);
    if (fields.includes('name')) return t('catalog.categories.nameTaken');
    if (fields.includes('parentId')) return t('catalog.categories.parentInvalid');
    return t('catalog.categories.saveError');
  };

  const openCreate = () => {
    setEditing(null);
    setName('');
    setNameAr('');
    setParentId('');
    setIsCreating(true);
  };

  const openEdit = (category: ProductCategorySummary) => {
    setIsCreating(false);
    setEditing(category);
    setName(category.name);
    setNameAr(category.nameAr ?? '');
    setParentId(category.parentId ?? '');
  };

  const closeDialog = () => {
    setIsCreating(false);
    setEditing(null);
    create.reset();
    update.reset();
  };

  const save = () => {
    if (!name.trim()) return;
    if (editing) {
      update.mutate(
        // Null clears: the API reads an empty string as "leave unchanged".
        {
          id: editing.id,
          name: name.trim(),
          nameAr: nameAr.trim() || null,
          parentId: parentId || null,
        },
        {
          onSuccess: (saved) => {
            setFeedback(t('catalog.categories.saved', { name: display(saved) }));
            closeDialog();
          },
        },
      );
      return;
    }
    create.mutate(
      {
        name: name.trim(),
        ...(nameAr.trim() ? { nameAr: nameAr.trim() } : {}),
        ...(parentId ? { parentId } : {}),
      },
      {
        onSuccess: (saved) => {
          setFeedback(t('catalog.categories.added', { name: display(saved) }));
          closeDialog();
        },
      },
    );
  };

  const toggle = (category: ProductCategorySummary) =>
    update.mutate(
      { id: category.id, isActive: !category.isActive },
      {
        onSuccess: (saved) =>
          setFeedback(
            saved.isActive
              ? t('catalog.categories.reactivated', { name: display(saved) })
              : t('catalog.categories.deactivated', { name: display(saved) }),
          ),
      },
    );

  // One level of nesting: a parent is a top-level category other than the one being edited.
  const parentOptions = [
    { value: '', label: t('catalog.categories.noParent') },
    ...(everything?.items ?? [])
      .filter((category) => category.parentId === null && category.id !== editing?.id)
      .map((category) => ({ value: category.id, label: display(category) })),
  ];

  const columns: Column<ProductCategorySummary>[] = [
    {
      key: 'name',
      header: t('catalog.categories.fields.name'),
      primary: true,
      render: (category) => (
        <span className="flex flex-col">
          <span className="text-ink font-medium" dir="auto">
            {category.name}
          </span>
          <span className="text-muted text-xs" dir="auto">
            {category.nameAr ?? '—'}
          </span>
        </span>
      ),
    },
    {
      key: 'parent',
      header: t('catalog.categories.fields.parent'),
      render: (category) => (
        <span className="text-muted" dir="auto">
          {category.parentName
            ? localizedName(category.parentName, category.parentNameAr, i18n.language)
            : '—'}
        </span>
      ),
    },
    {
      key: 'products',
      header: t('catalog.categories.fields.products'),
      align: 'end',
      render: (category) => (
        <span className="text-muted" dir="ltr">
          {category.productCount}
        </span>
      ),
    },
    {
      key: 'status',
      header: t('catalog.fields.status'),
      render: (category) => (
        <span className={category.isActive ? 'text-muted text-sm' : 'text-amber-700 text-sm'}>
          {category.isActive ? t('catalog.categories.inUse') : t('catalog.categories.outOfUse')}
        </span>
      ),
    },
    ...(canEdit
      ? [
          {
            key: 'actions',
            header: t('common.actions'),
            align: 'end' as const,
            render: (category: ProductCategorySummary) => (
              <span className="flex flex-wrap items-center justify-end gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={t('catalog.categories.editNamed', { name: display(category) })}
                  onClick={() => openEdit(category)}
                >
                  {t('common.edit')}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={
                    category.isActive
                      ? t('catalog.categories.deactivateNamed', { name: display(category) })
                      : t('catalog.categories.reactivateNamed', { name: display(category) })
                  }
                  onClick={() => toggle(category)}
                  disabled={update.isPending}
                >
                  {category.isActive
                    ? t('catalog.categories.deactivate')
                    : t('catalog.categories.reactivate')}
                </Button>
              </span>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('catalog.categories.title')}
        subtitle={t('catalog.categories.subtitle')}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" onClick={() => void navigate('/inventory/products')}>
              {t('catalog.categories.backToProducts')}
            </Button>
            {canCreate ? <Button onClick={openCreate}>{t('catalog.categories.add')}</Button> : null}
          </div>
        }
      />

      {feedback ? <Alert tone="success">{feedback}</Alert> : null}
      {update.isError && !editing ? (
        <Alert tone="error">{describeSaveError(update.error)}</Alert>
      ) : null}

      <Card>
        <CardHeader
          title={t('catalog.categories.title')}
          subtitle={
            data ? t('catalog.categories.count', { count: data.pagination.totalItems }) : undefined
          }
        />

        <div className="border-line grid gap-3 border-b px-4 py-3 sm:grid-cols-2">
          <Input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('catalog.categories.searchPlaceholder')}
            aria-label={t('catalog.categories.searchLabel')}
          />
          <Select
            options={[
              { value: 'all', label: t('catalog.categories.filters.all') },
              { value: 'true', label: t('catalog.categories.filters.inUse') },
              { value: 'false', label: t('catalog.categories.filters.outOfUse') },
            ]}
            value={active}
            aria-label={t('catalog.fields.status')}
            onChange={(event) => setActive(event.target.value as 'all' | 'true' | 'false')}
          />
        </div>

        <DataTable
          caption={t('catalog.categories.title')}
          columns={columns}
          rows={data?.items}
          rowKey={(category) => category.id}
          isLoading={isPending}
          isError={isError}
          errorMessage={t('catalog.categories.loadError')}
          onRetry={() => void refetch()}
          emptyIcon={Tags}
          emptyTitle={t('catalog.categories.empty')}
          emptyDescription={t('catalog.categories.emptyHint')}
        />
      </Card>

      <Modal
        isOpen={isCreating || editing !== null}
        onClose={closeDialog}
        title={editing ? t('catalog.categories.editTitle') : t('catalog.categories.addTitle')}
        footer={
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button variant="secondary" onClick={closeDialog} disabled={saving}>
              {t('common.cancel')}
            </Button>
            <Button onClick={save} isLoading={saving} disabled={!name.trim() || saving}>
              {t('common.save')}
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          {saveError ? <Alert tone="error">{describeSaveError(saveError)}</Alert> : null}
          <Input
            label={t('catalog.categories.fields.name')}
            required
            value={name}
            hint={t('catalog.categories.fields.nameHint')}
            onChange={(event) => setName(event.target.value)}
          />
          <Input
            label={t('catalog.categories.fields.nameAr')}
            dir="rtl"
            value={nameAr}
            onChange={(event) => setNameAr(event.target.value)}
          />
          <FormField
            label={t('catalog.categories.fields.parent')}
            hint={t('catalog.categories.fields.parentHint')}
          >
            {({ id, describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                options={parentOptions}
                value={parentId}
                onChange={(event) => setParentId(event.target.value)}
              />
            )}
          </FormField>
        </div>
      </Modal>
    </div>
  );
}
